import { createTheme } from '@mui/material/styles';
import { colors, typography as typographySettings, spacing, borderRadius, shadows } from './styles/styleConstants';

// Create a mobile-optimized theme with our new design system
const theme = createTheme({
  palette: {
    primary: {
      main: colors.darkSlateBlue,
      light: colors.mediumSlateBlue,
      dark: colors.charcoal,
    },
    secondary: {
      main: colors.actionBlue,
      light: colors.lightGray,
      dark: colors.darkSlateBlue,
    },
    background: {
      default: colors.backgroundColor,
      paper: colors.softWhite,
    },
    text: {
      primary: colors.charcoal,
      secondary: colors.mediumSlateBlue,
      disabled: 'rgba(0, 0, 0, 0.38)',
    },
    error: {
      main: colors.danger,
    },
    warning: {
      main: colors.warning,
    },
    info: {
      main: colors.info,
    },
    success: {
      main: colors.success,
    },
  },
  typography: {
    fontFamily: typographySettings.fontFamily.primary,
    h1: {
      fontFamily: typographySettings.fontFamily.heading,
      fontSize: typographySettings.fontSizes['3xl'],
      fontWeight: typographySettings.fontWeights.semibold,
    },
    h2: {
      fontFamily: typographySettings.fontFamily.heading,
      fontSize: typographySettings.fontSizes['2xl'],
      fontWeight: typographySettings.fontWeights.semibold,
    },
    h3: {
      fontFamily: typographySettings.fontFamily.heading,
      fontSize: typographySettings.fontSizes.xl,
      fontWeight: typographySettings.fontWeights.medium,
    },
    h4: {
      fontFamily: typographySettings.fontFamily.heading,
      fontSize: typographySettings.fontSizes.lg,
      fontWeight: typographySettings.fontWeights.medium,
    },
    h5: {
      fontFamily: typographySettings.fontFamily.heading,
      fontSize: typographySettings.fontSizes.md,
      fontWeight: typographySettings.fontWeights.medium,
    },
    h6: {
      fontFamily: typographySettings.fontFamily.heading,
      fontSize: typographySettings.fontSizes.base,
      fontWeight: typographySettings.fontWeights.medium,
    },
    subtitle1: {
      fontSize: typographySettings.fontSizes.md,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: typographySettings.fontSizes.sm,
      fontWeight: typographySettings.fontWeights.medium,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: typographySettings.fontSizes.base,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: typographySettings.fontSizes.sm,
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none', // Avoid all caps buttons
      fontWeight: typographySettings.fontWeights.medium,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.backgroundColor,
          color: colors.charcoal,
          overscrollBehavior: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.md,
          padding: `${spacing.sm} ${spacing.lg}`,
          minHeight: '48px', // Larger touch target for mobile
          transition: 'all 0.2s',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: shadows.md,
          },
        },
        containedPrimary: {
          backgroundColor: colors.actionBlue,
          boxShadow: shadows.sm,
        },
        containedSecondary: {
          backgroundColor: colors.mediumSlateBlue,
          boxShadow: shadows.sm,
        },
        outlinedPrimary: {
          borderColor: colors.actionBlue,
          color: colors.actionBlue,
        },
        text: {
          '&:hover': {
            backgroundColor: 'rgba(59, 130, 246, 0.04)',
            transform: 'none',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.lg,
          boxShadow: shadows.md,
          transition: 'all 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: shadows.lg,
          },
          overflow: 'hidden',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: spacing.lg,
          '&:last-child': {
            paddingBottom: spacing.lg,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          marginBottom: spacing.md,
          '& .MuiOutlinedInput-root': {
            borderRadius: borderRadius.md,
            borderColor: colors.mediumSlateBlue,
            '&.Mui-focused': {
              borderColor: colors.actionBlue,
            },
          },
          '& .MuiInputLabel-root': {
            color: colors.mediumSlateBlue,
          },
          '& .MuiInputBase-input': {
            padding: spacing.md,
          },
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          height: 64, // Taller for better touch targets
          backgroundColor: colors.softWhite,
          borderTop: `1px solid ${colors.divider}`,
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          paddingTop: spacing.sm,
          paddingBottom: spacing.sm,
          color: colors.mediumSlateBlue,
          '&.Mui-selected': {
            color: colors.actionBlue,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: shadows.md,
          backgroundColor: colors.softWhite,
          color: colors.charcoal,
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: shadows.lg,
          backgroundColor: colors.actionBlue,
          '&:hover': {
            backgroundColor: colors.actionBlue,
            transform: 'scale(1.05)',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: borderRadius.lg,
          boxShadow: shadows.xl,
          padding: spacing.md,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: spacing.lg,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: spacing.lg,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: spacing.lg,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          margin: `${spacing.md} 0`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.md,
          backgroundColor: 'rgba(94, 108, 132, 0.1)',
          margin: spacing.xs,
          padding: `${spacing.xs} ${spacing.sm}`,
        },
        label: {
          padding: `${spacing.xs} ${spacing.sm}`,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.mediumSlateBlue,
          color: colors.softWhite,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.sm,
          '&:hover': {
            backgroundColor: 'rgba(94, 108, 132, 0.05)',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: colors.actionBlue,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: typographySettings.fontWeights.medium,
          '&.Mui-selected': {
            color: colors.actionBlue,
          },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          backgroundColor: colors.actionBlue,
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.darkSlateBlue,
          borderRadius: borderRadius.sm,
          padding: `${spacing.xs} ${spacing.sm}`,
          fontSize: typographySettings.fontSizes.xs,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: colors.softWhite,
          backgroundImage: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.softWhite,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: colors.mediumSlateBlue,
          minWidth: 40,
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: '1.25rem',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: colors.mediumSlateBlue,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginBottom: spacing.xs,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: colors.mediumSlateBlue,
          '&.Mui-checked': {
            color: colors.actionBlue,
          },
        },
        track: {
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          '.Mui-checked.Mui-checked + &': {
            backgroundColor: 'rgba(59, 130, 246, 0.5)',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.md,
        },
      },
    },
    // MuiChip styles are defined above
  },
});

export default theme;
